import { useEffect, useMemo, useRef, useState, type RefObject } from "react"
import {
  GitBranch,
  Pause,
  Play,
  RotateCcw,
  StepBack,
  StepForward,
  XCircle,
  ZoomIn,
  ZoomOut,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/cn"
import type { ChatBubble, WorkflowTraceEvent, WorkflowTracePayload, WorkflowTraceRequirement } from "@/types/test_lab"

export type WorkflowGraphOutline = {
  engine?: string | null
  version?: number | string | null
  purpose?: string
  trigger?: Record<string, unknown>
  scope?: string
  blocked_interrupts?: string[]
  policies?: Record<string, WorkflowGraphPolicy>
  start_actions?: WorkflowGraphAction[]
  interrupts?: WorkflowGraphInterrupt[]
  cards?: WorkflowGraphCard[]
}

export type WorkflowGraphPolicy = {
  rules?: WorkflowGraphPolicyRule[]
}

export type WorkflowGraphPolicyRule = {
  name?: string
  when?: unknown
  when_summary?: string
  verdict?: string
  then?: Record<string, unknown>
}

export type WorkflowGraphInterrupt = {
  id: string
  purpose?: string
  card?: string
  start_skill?: string
  start_card?: string
  trigger?: Record<string, unknown>
}

export type WorkflowGraphCard = {
  id: string
  purpose?: string
  priority?: string | number | null
  preconditions_present?: boolean
  preconditions_summary?: string
  phases?: WorkflowGraphPhase[]
}

export type WorkflowGraphPhase = {
  id: string
  purpose?: string
  type?: string
  response_mode?: string
  next?: string
  end_status?: string
  required_fields?: string[]
  optional_fields?: string[]
  required_upload?: Record<string, unknown>
  tools?: string[]
  actions?: WorkflowGraphAction[]
  branches?: WorkflowGraphBranch[]
}

export type WorkflowGraphAction = {
  id: string
  purpose?: string
  type?: string
  tool?: string
  endpoint?: string
  policy?: string
  matcher?: string
  preconditions_present?: boolean
}

export type WorkflowGraphBranch = {
  kind?: string
  index?: number
  purpose?: string
  next?: string
  predicate?: string
}

type Props = {
  outline?: WorkflowGraphOutline | null
  trace?: WorkflowTracePayload | null
  messages?: ChatBubble[]
  title?: string
  subtitle?: string
  height?: number
  playback?: boolean
  className?: string
}

const PLAYBACK_SPEEDS = [
  { label: "0.25s", value: 250 },
  { label: "0.5s", value: 500 },
  { label: "1s", value: 1000 },
  { label: "2s", value: 2000 },
] as const

type PlaybackSpeedMs = typeof PLAYBACK_SPEEDS[number]["value"]
const PLAYBACK_SPEED_STORAGE_KEY = "workflowReplaySpeedMs"
const DEFAULT_PLAYBACK_SPEED_MS: PlaybackSpeedMs = 500

export function WorkflowGraphPanel({
  outline,
  trace,
  messages = [],
  title = "Workflow map",
  subtitle,
  height = 520,
  playback = false,
  className,
}: Props) {
  const rawEvents = Array.isArray(trace?.trace) ? trace.trace : []
  const frames = useMemo(() => buildReplayFrames(rawEvents, messages), [rawEvents, messages])
  const [frameIndex, setFrameIndex] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [playbackSpeedMs, setPlaybackSpeedMs] = useState<PlaybackSpeedMs>(() => readStoredPlaybackSpeed())

  useEffect(() => {
    setFrameIndex(Math.max(frames.length - 1, 0))
    setPlaying(false)
  }, [frames.length])

  useEffect(() => {
    if (!playing || frames.length <= 1) return
    const interval = window.setInterval(() => {
      setFrameIndex((current) => {
        if (current >= frames.length - 1) {
          window.clearInterval(interval)
          setPlaying(false)
          return current
        }
        return current + 1
      })
    }, playbackSpeedMs)
    return () => window.clearInterval(interval)
  }, [frames.length, playbackSpeedMs, playing])

  const visibleTrace = useMemo(() => {
    if (!playback || frames.length === 0) return trace
    const frame = frames[Math.min(frameIndex, frames.length - 1)]
    const visibleEvents = rawEvents.slice(0, frame.eventCount)
    return trace ? { ...trace, trace: visibleEvents, state: replayStateForEvents(visibleEvents) } : null
  }, [frameIndex, frames, playback, rawEvents, trace])

  const runtimeOutline = useMemo(() => {
    return outline ?? normalizeWorkflowGraphOutline(visibleTrace?.outline)
  }, [outline, visibleTrace?.outline])
  const mermaidGraph = useMemo(() => buildMermaidGraph(runtimeOutline, visibleTrace), [runtimeOutline, visibleTrace])
  const graphModeNote = graphModeNoteFor(runtimeOutline)

  const currentFrame = frames[Math.min(frameIndex, Math.max(frames.length - 1, 0))]

  return (
    <section className={cn("rounded-card border border-border bg-app-panel p-4", className)}>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-app-text">
            <GitBranch size={15} className="text-brand-red" />
            {title}
          </div>
          {subtitle && <p className="mt-1 max-w-3xl text-xs text-app-muted">{subtitle}</p>}
          {graphModeNote && <p className="mt-1 max-w-3xl text-[11px] text-app-subtle">{graphModeNote}</p>}
        </div>
        <GraphLegend />
      </div>

      {playback && frames.length > 1 && (
        <div className="mb-3 rounded-control border border-border bg-app-bg px-3 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setPlaying(false)
                setFrameIndex(0)
              }}
            >
              <RotateCcw size={13} />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setPlaying(false)
                setFrameIndex((i) => Math.max(i - 1, 0))
              }}
            >
              <StepBack size={13} />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="red"
              onClick={() => {
                if (frameIndex >= frames.length - 1) setFrameIndex(0)
                setPlaying((value) => !value)
              }}
            >
              {playing ? <Pause size={13} /> : <Play size={13} />}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setPlaying(false)
                setFrameIndex((i) => Math.min(i + 1, frames.length - 1))
              }}
            >
              <StepForward size={13} />
            </Button>
            <input
              type="range"
              min={0}
              max={frames.length - 1}
              value={frameIndex}
              onChange={(event) => {
                setPlaying(false)
                setFrameIndex(Number(event.target.value))
              }}
              className="min-w-[180px] flex-1 accent-brand-red"
              aria-label="Workflow replay position"
            />
            <div className="min-w-[210px] text-xs text-app-muted">
              <span className="text-app-text">{frameIndex + 1}/{frames.length}</span>
              {currentFrame?.label && <span className="ml-2">{currentFrame.label}</span>}
            </div>
            <div className="flex items-center gap-1 rounded-control border border-border bg-app-panel p-1" aria-label="Workflow replay speed">
              {PLAYBACK_SPEEDS.map((speed) => (
                <button
                  key={speed.value}
                  type="button"
                  className={cn(
                    "rounded-chip px-2 py-1 text-[11px] font-semibold transition",
                    playbackSpeedMs === speed.value
                      ? "bg-brand-red text-white shadow-sm shadow-brand-red/20"
                      : "text-app-muted hover:bg-white/[0.06] hover:text-app-text",
                  )}
                  onClick={() => {
                    setPlaybackSpeedMs(speed.value)
                    storePlaybackSpeed(speed.value)
                  }}
                >
                  {speed.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className={cn(playback && messages.length > 0 && "grid gap-3 xl:grid-cols-[360px_minmax(0,1fr)]")}>
        {playback && messages.length > 0 && (
          <ReplayMessageRail
            messages={messages}
            currentIndex={typeof currentFrame?.messageIndex === "number" ? currentFrame.messageIndex : -1}
            height={height}
          />
        )}
        <MermaidConversationFlow diagram={mermaidGraph.diagram} nodeDetails={mermaidGraph.details} height={height} />
      </div>
    </section>
  )
}

function ReplayMessageRail({ messages, currentIndex, height }: { messages: ChatBubble[]; currentIndex: number; height: number }) {
  const listRef = useRef<HTMLOListElement | null>(null)

  useEffect(() => {
    const list = listRef.current
    const current = list?.querySelector("[data-current='true']")
    if (!list || !current) return

    scrollElementIntoContainer(current, list)
  }, [currentIndex])

  return (
    <aside className="overflow-hidden rounded-control border border-border bg-app-bg" style={{ height }}>
      <div className="border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-app-muted">
        Conversation
      </div>
      <ol ref={listRef} className="thin-scrollbar h-[calc(100%-37px)] space-y-2 overflow-y-auto p-3">
        {messages.map((message, index) => {
          const current = index === currentIndex
          const future = currentIndex >= 0 && index > currentIndex
          return (
            <li
              key={`${message.id}-${index}`}
              data-current={current ? "true" : undefined}
              className={cn(
                "rounded-control border px-3 py-2 transition",
                current ? "border-brand-red/60 bg-brand-redSoft/15" : "border-border bg-white/[0.03]",
                future && "opacity-45",
              )}
            >
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className={cn("text-xs font-semibold", current ? "text-brand-red" : "text-app-text")}>
                  {messageLabel(message)}
                </span>
                {typeof message.turnNumber === "number" && (
                  <span className="text-[10px] text-app-subtle">turn {message.turnNumber}</span>
                )}
              </div>
              <p className="line-clamp-4 whitespace-pre-wrap text-xs leading-relaxed text-app-muted">
                {message.content?.trim() || toolOnlyMessageLabel(message)}
              </p>
            </li>
          )
        })}
      </ol>
    </aside>
  )
}

function MermaidConversationFlow({ diagram, nodeDetails, height }: { diagram: string; nodeDetails: MermaidNodeDetail[]; height: number }) {
  const [svg, setSvg] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [selectedDetail, setSelectedDetail] = useState<MermaidNodeDetail | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLElement | null>(null)
  const renderId = useMemo(() => `workflow-mermaid-${Math.abs(hashString(diagram))}`, [diagram])
  const detailById = useMemo(() => new Map(nodeDetails.map((detail) => [detail.id, detail])), [nodeDetails])
  const orderedNodeDetails = useMemo(() => Array.from(detailById.values()).sort((a, b) => b.id.length - a.id.length), [detailById])

  function detailForEventTarget(target: EventTarget | null) {
    if (!(target instanceof Element)) return null
    const group = target.closest("svg g.node")
    if (!group || !contentRef.current?.contains(group)) return null

    return orderedNodeDetails.find((detail) => group.id.includes(detail.id)) ?? null
  }

  useEffect(() => {
    let cancelled = false
    setSvg("")
    setError(null)

    if (!diagram.trim()) return

    import("mermaid")
      .then(({ default: mermaid }) => {
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: "base",
          themeVariables: {
            background: "#050505",
            mainBkg: "#18181b",
            primaryColor: "#18181b",
            primaryTextColor: "#f4f4f5",
            primaryBorderColor: "#a1a1aa",
            lineColor: "#a1a1aa",
            textColor: "#f4f4f5",
            fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
          },
          flowchart: {
            curve: "basis",
            htmlLabels: true,
            nodeSpacing: 58,
            rankSpacing: 70,
          },
        })
        return mermaid.render(renderId, diagram)
      })
      .then(({ svg: rendered }) => {
        if (!cancelled) setSvg(rendered)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not render Mermaid workflow.")
      })

    return () => {
      cancelled = true
    }
  }, [diagram, renderId])

  useEffect(() => {
    setSelectedDetail((current) => {
      if (!current) return null
      return detailById.has(current.id) ? detailById.get(current.id) ?? null : null
    })
  }, [detailById])

  useEffect(() => {
    const scroll = scrollRef.current
    const current = contentRef.current?.querySelector("svg .current")
    if (!scroll || !current) return

    window.requestAnimationFrame(() => scrollElementIntoContainer(current, scroll))
  }, [diagram, svg, zoom])

  useEffect(() => {
    const root = contentRef.current
    if (!root || !svg) return

    root.querySelectorAll<SVGGElement>("svg g.node").forEach((group) => {
      const detail = orderedNodeDetails.find((candidate) => group.id.includes(candidate.id))
      if (!detail) return

      group.style.cursor = "pointer"
      group.querySelectorAll<SVGElement>("*").forEach((child) => {
        child.style.cursor = "pointer"
      })
      group.setAttribute("role", "button")
      group.setAttribute("tabindex", "0")
    })
  }, [orderedNodeDetails, svg])

  useEffect(() => {
    if (!selectedDetail) return

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setSelectedDetail(null)
    }

    function closeOnOutsideClick(event: PointerEvent) {
      const target = event.target
      if (!(target instanceof Element)) return
      if (panelRef.current?.contains(target)) return
      if (target.closest("svg g.node")) return

      setSelectedDetail(null)
    }

    document.addEventListener("keydown", closeOnEscape)
    document.addEventListener("pointerdown", closeOnOutsideClick)
    return () => {
      document.removeEventListener("keydown", closeOnEscape)
      document.removeEventListener("pointerdown", closeOnOutsideClick)
    }
  }, [selectedDetail])

  if (!diagram.trim()) {
    return (
      <div className="flex items-center justify-center rounded-control border border-border bg-app-bg px-6 text-center text-sm text-app-muted" style={{ height }}>
        No workflow graph data is available yet.
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-control border border-status-error/40 bg-status-error/10 p-4 text-sm text-status-error" style={{ minHeight: height }}>
        Mermaid render failed: {error}
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden rounded-control border border-border bg-[#050505]" style={{ height }}>
      {svg && (
        <div className="absolute left-3 top-3 z-10 rounded-control border border-border bg-app-panel/90 px-2 py-1 text-[11px] font-medium text-app-muted shadow-lg shadow-black/30 backdrop-blur">
          Click nodes for details
        </div>
      )}
      <div className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-control border border-border bg-app-panel/90 p-1 shadow-lg shadow-black/30 backdrop-blur">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          title="Zoom out"
          onClick={() => setZoom((value) => Math.max(0.25, Number((value - 0.1).toFixed(2))))}
        >
          <ZoomOut size={13} />
        </Button>
        <button
          type="button"
          className="min-w-12 rounded-chip px-2 py-1 text-[11px] font-semibold text-app-muted hover:text-app-text"
          title="Reset zoom"
          onClick={() => setZoom(1)}
        >
          {Math.round(zoom * 100)}%
        </button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          title="Zoom in"
          onClick={() => setZoom((value) => Math.min(1.6, Number((value + 0.1).toFixed(2))))}
        >
          <ZoomIn size={13} />
        </Button>
      </div>
      <div ref={scrollRef} className="thin-scrollbar h-full overflow-auto p-4 pt-14">
        {svg ? (
          <div
            ref={contentRef}
            className="[&_svg]:mx-auto [&_svg]:max-w-none [&_svg_g.node]:cursor-pointer [&_svg_g.node_*]:cursor-pointer"
            style={{ transform: `scale(${zoom})`, transformOrigin: "top center" }}
            onClick={(event) => {
              const detail = detailForEventTarget(event.target)
              if (detail) setSelectedDetail(detail)
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return
              const detail = detailForEventTarget(event.target)
              if (!detail) return

              event.preventDefault()
              setSelectedDetail(detail)
            }}
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-app-muted">Rendering workflow...</div>
        )}
      </div>
      {selectedDetail && (
        <NodeDetailPanel detail={selectedDetail} panelRef={panelRef} onClose={() => setSelectedDetail(null)} />
      )}
    </div>
  )
}

function NodeDetailPanel({ detail, panelRef, onClose }: { detail: MermaidNodeDetail; panelRef: RefObject<HTMLElement | null>; onClose: () => void }) {
  return (
    <aside ref={panelRef} className="absolute bottom-3 right-3 z-10 max-h-[calc(100%-5.5rem)] w-[min(440px,calc(100%-1.5rem))] overflow-auto rounded-control border border-border bg-app-panel/95 p-3 shadow-2xl shadow-black/50 backdrop-blur">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-app-text">{detail.title}</div>
          {detail.subtitle && <div className="mt-0.5 text-xs text-app-muted">{detail.subtitle}</div>}
        </div>
        <button type="button" className="rounded-control p-1 text-app-muted hover:bg-white/[0.06] hover:text-app-text" onClick={onClose} aria-label="Close node details">
          <XCircle size={15} />
        </button>
      </div>

      {detail.description && <p className="mb-3 text-xs leading-relaxed text-app-muted">{detail.description}</p>}

      {detail.rows.length > 0 && (
        <div className="mb-3 space-y-1.5">
          {detail.rows.map((row) => (
            <div key={row.label} className="grid grid-cols-[110px_minmax(0,1fr)] gap-2 text-xs">
              <span className="text-app-subtle">{row.label}</span>
              <code className="min-w-0 break-words rounded bg-black/20 px-1.5 py-0.5 text-app-text">{formatDetailValue(row.value)}</code>
            </div>
          ))}
        </div>
      )}

      {detail.sections.map((section) => (
        <div key={section.title} className="mt-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-app-subtle">{section.title}</div>
          <pre className="max-h-44 overflow-auto whitespace-pre-wrap break-all rounded-control border border-border bg-black/25 p-2 text-[10px] leading-relaxed text-app-muted">
            {JSON.stringify(section.value, null, 2)}
          </pre>
        </div>
      ))}
    </aside>
  )
}

function GraphLegend() {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] text-app-muted">
      <LegendDot className="bg-sky-400" label="current" />
      <LegendDot className="bg-status-success" label="visited" />
      <LegendDot className="bg-status-error" label="blocked" />
    </div>
  )
}

function LegendDot({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn("h-2 w-2 rounded-full", className)} />
      {label}
    </span>
  )
}

function readStoredPlaybackSpeed(): PlaybackSpeedMs {
  if (typeof window === "undefined") return DEFAULT_PLAYBACK_SPEED_MS
  const stored = Number(window.localStorage.getItem(PLAYBACK_SPEED_STORAGE_KEY))
  const match = PLAYBACK_SPEEDS.find((speed) => speed.value === stored)
  return match?.value ?? DEFAULT_PLAYBACK_SPEED_MS
}

function storePlaybackSpeed(value: PlaybackSpeedMs) {
  if (typeof window === "undefined") return
  window.localStorage.setItem(PLAYBACK_SPEED_STORAGE_KEY, String(value))
}

function scrollElementIntoContainer(element: Element, container: HTMLElement) {
  const elementRect = element.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()
  const top = container.scrollTop + elementRect.top - containerRect.top - container.clientHeight / 2 + elementRect.height / 2
  const left = container.scrollLeft + elementRect.left - containerRect.left - container.clientWidth / 2 + elementRect.width / 2

  container.scrollTo({
    top: Math.max(top, 0),
    left: Math.max(left, 0),
    behavior: "smooth",
  })
}

function compactDetailRows(rows: Array<[string, unknown]>): MermaidNodeDetail["rows"] {
  return rows
    .filter(([, value]) => {
      if (value == null) return false
      if (typeof value === "string") return value.trim().length > 0
      if (Array.isArray(value)) return value.length > 0
      if (typeof value === "object") return Object.keys(asRecord(value)).length > 0
      return true
    })
    .map(([label, value]) => ({ label, value }))
}

function formatDetailValue(value: unknown): string {
  if (value == null) return ""
  if (typeof value === "boolean") return value ? "yes" : "no"
  if (typeof value === "number") return String(value)
  if (typeof value === "string") return value
  if (Array.isArray(value)) return value.map((entry): string => formatDetailValue(entry)).join(", ")
  return JSON.stringify(value)
}

function buildReplayFrames(events: WorkflowTraceEvent[], messages: ChatBubble[]) {
  const firstFrame = {
    eventCount: 0,
    messageIndex: null,
    label: "before workflow",
  }

  if (messages.length > 0) {
    const messageFrames = messages.map((message, index) => {
      const turnNumber = typeof message.turnNumber === "number" ? message.turnNumber : null
      const eventCount = turnNumber == null
        ? Math.min(events.length, index + 1)
        : events.filter((event) => (event.turn_number ?? -1) <= turnNumber).length
      return {
        eventCount,
        messageIndex: index,
        label: `${message.role} turn${turnNumber == null ? "" : ` ${turnNumber}`}`,
      }
    })
    return [firstFrame, ...messageFrames]
  }
  return [firstFrame, ...events.map((event, index) => ({
    eventCount: index + 1,
    messageIndex: null,
    label: `${formatEventName(event.event)} #${event.seq}`,
  }))]
}

function replayStateForEvents(events: WorkflowTraceEvent[]) {
  const state: Record<string, unknown> = {}
  events.forEach((event) => {
    const details = asRecord(event.details)
    const selected = selectedCandidateFromTrace(events, {})

    if (event.event === "workflow_candidates_scored") {
      setReplayStateValue(state, "active_skill_id", selected.skill_id)
      setReplayStateValue(state, "active_skill_version_id", details.selected_skill_version_id || selected.skill_version_id)
    }

    if (event.event === "workflow_activated" || event.event === "agent_workflow_activated") {
      setReplayStateValue(state, "active_workflow_scope", details.active_workflow_scope)
      setReplayStateValue(state, "active_skill_id", details.active_skill_id || selected.skill_id)
      setReplayStateValue(state, "active_skill_version_id", details.active_skill_version_id || selected.skill_version_id)
    }

    if (event.event === "skill_card_activated") {
      setReplayStateValue(state, "active_card_id", details.skill_key || details.card_id || details.active_card_id)
    }

    if (event.event === "card_ended") {
      setReplayStateValue(state, "active_card_id", details.active_card_id)
      setReplayStateValue(state, "card_status", "ended")
      setReplayStateValue(state, "last_end_status", details.end_status)
    }

    if (cardStateEvent(event)) {
      setReplayStateValue(state, "active_card_id", details.active_card_id)
      setReplayStateValue(state, "card_status", details.card_status)
    }
  })
  return state
}

function cardStateEvent(event: WorkflowTraceEvent) {
  return [
    "action_started",
    "action_completed",
    "action_failed",
    "model_tool_action_waiting",
    "model_tool_action_completed",
    "collect_fields_matched",
  ].includes(event.event)
}

function setReplayStateValue(state: Record<string, unknown>, key: string, value: unknown) {
  if (value == null) return
  if (typeof value === "string" && value.trim() === "") return
  state[key] = value
}

function isPolicyEvent(event: WorkflowTraceEvent) {
  return isPolicyDetails(asRecord(event.details), asRecord(event.details?.result))
}

function isPolicyDetails(details: Record<string, unknown>, result: Record<string, unknown>) {
  return details.action_type === "policy" || details.tool === "policy" || result.tool === "policy" || !!details.policy || !!result.policy
}

type PolicySummary = { label: string; verdict?: string | null; inputs?: unknown; result?: unknown }

function policySummary(event: WorkflowTraceEvent): PolicySummary {
  const details = asRecord(event.details)
  const result = asRecord(details.result)
  const data = asRecord(result.data)
  return {
    label: humanize(stringish(result.policy || details.policy) || "policy"),
    verdict: stringish(data.verdict),
    inputs: data.policy_inputs || result.inputs || details.inputs,
    result: Object.keys(data).length > 0 ? data : result,
  }
}

type MermaidTone = "idle" | "visited" | "current" | "blocked" | "warning"
type MermaidShape = "circle" | "rect" | "data" | "decision"

type MermaidNode = {
  id: string
  key: string
  label: string
  shape: MermaidShape
  tone: MermaidTone
  detail?: MermaidNodeDetail
}

type MermaidEdge = {
  from: string
  to: string
  label?: string
  active?: boolean
}

type MermaidGraph = {
  diagram: string
  details: MermaidNodeDetail[]
}

type MermaidNodeDetail = {
  id: string
  title: string
  subtitle?: string
  description?: string
  rows: Array<{ label: string; value: unknown }>
  sections: Array<{ title: string; value: unknown }>
}

function buildMermaidGraph(outline: WorkflowGraphOutline | null | undefined, trace: WorkflowTracePayload | null | undefined): MermaidGraph {
  if (outline && Array.isArray(outline.cards) && outline.cards.length > 0) {
    if (outline.scope === "agent") return buildMermaidFromAgentOutline(outline, trace)
    return buildMermaidFromOutline(outline, trace)
  }
  return buildMermaidFromTrace(trace)
}

// Keep the two Mermaid modes separate:
// - skill workflows are trigger-first phase flows
// - agent-scoped test runs show routing/state-gate entry into cards
function graphModeNoteFor(outline: WorkflowGraphOutline | null | undefined) {
  if (String(outline?.engine ?? "") === "workflow_v3") {
    return "WorkflowV3 graph mode: planner, data loaders, field/file collection, policies, actions, and reply outcomes are shown as runtime phases."
  }
  if (outline?.scope === "agent") {
    return "Agent routing graph mode: interrupts and state gates route into cards. Skill graph mode: one skill trigger flows through phases."
  }
  return null
}

function buildMermaidFromAgentOutline(outline: WorkflowGraphOutline, trace: WorkflowTracePayload | null | undefined) {
  const cards = Array.isArray(outline.cards) ? outline.cards : []
  if (cards.length === 0) return emptyMermaidGraph()

  const state = trace?.state ?? {}
  const rawVisitedPhaseIds = visitedPhases(trace)
  const rawActiveCard = stringish(state.active_card_id)
  const cardEnded = stringish(state.card_status) === "ended"
  const workflowVisited = workflowTriggered(trace) || rawVisitedPhaseIds.size > 0 || !!rawActiveCard
  const selectedCard = workflowVisited ? rawActiveCard || selectedCardFromTrace(trace) : null
  const activePhase = null
  const visitedPhaseIds = workflowVisited ? rawVisitedPhaseIds : new Set<string>()
  const latestRequirements = requirementsByPhase(trace)
  const nodes: MermaidNode[] = []
  const edges: MermaidEdge[] = []
  const cardById = new Map(cards.map((card) => [card.id, card]))
  const routedCardIds = new Set<string>()

  const cardNodeId = (card: WorkflowGraphCard) => addMermaidNode(nodes, {
    key: `card:${card.id}`,
    label: joinMermaidLines([humanize(card.id), "card"]),
    shape: "rect",
    tone: card.id === selectedCard ? "visited" : "idle",
    detail: cardNodeDetail(card, card.id === selectedCard),
  })

  const targetCardNodeId = (target: string) => {
    const card = cardById.get(target)
    if (card) return cardNodeId(card)
    return addMermaidNode(nodes, {
      key: `missing_card:${target}`,
      label: joinMermaidLines(["Missing card:", humanize(target)]),
      shape: "rect",
      tone: "warning",
    })
  }

  const interrupts = Array.isArray(outline.interrupts) ? outline.interrupts : []
  if (interrupts.length > 0) {
    const hubId = addMermaidNode(nodes, {
      key: "agent:interrupts",
      label: joinMermaidLines(["Global interrupts", "runtime checks"]),
      shape: "rect",
      tone: workflowVisited ? "visited" : "idle",
      detail: agentHubNodeDetail("agent:interrupts", "Global interrupts", "Runtime checks that can route out of normal skill execution into an agent card."),
    })

    interrupts.forEach((interrupt) => {
      const target = interruptTargetCard(interrupt)
      const nodeId = addMermaidNode(nodes, {
        key: `interrupt:${interrupt.id}`,
        label: joinMermaidLines([interruptTitle(interrupt), interruptTriggerLine(interrupt)]),
        shape: "decision",
        tone: target && target === selectedCard ? "visited" : "idle",
        detail: interruptNodeDetail(interrupt, target === selectedCard),
      })
      edges.push({ from: hubId, to: nodeId })
      if (target) {
        routedCardIds.add(target)
        edges.push({ from: nodeId, to: targetCardNodeId(target), label: "starts card", active: target === selectedCard })
      }
    })
  }

  const gatedCards = cards.filter((card) => card.preconditions_present)
  if (gatedCards.length > 0) {
    const hubId = addMermaidNode(nodes, {
      key: "agent:state_gates",
      label: joinMermaidLines(["State gates", "conversation/appuser checks"]),
      shape: "rect",
      tone: workflowVisited ? "visited" : "idle",
      detail: agentHubNodeDetail("agent:state_gates", "State gates", "Conversation and appuser state checks that enter protected agent cards before normal skill help."),
    })

    gatedCards.forEach((card) => {
      const gateId = addMermaidNode(nodes, {
        key: `gate:${card.id}`,
        label: joinMermaidLines([humanize(card.id), card.preconditions_summary || "entrance rule"]),
        shape: "decision",
        tone: card.id === selectedCard ? "visited" : "idle",
        detail: gateNodeDetail(card, card.id === selectedCard),
      })
      routedCardIds.add(card.id)
      edges.push({ from: hubId, to: gateId })
      edges.push({ from: gateId, to: cardNodeId(card), label: "matches", active: card.id === selectedCard })
    })
  }

  const unroutedCards = cards.filter((card) => !routedCardIds.has(card.id))
  if (unroutedCards.length > 0) {
    const hubId = addMermaidNode(nodes, {
      key: "agent:callable_cards",
      label: joinMermaidLines(["Callable cards", "available to routing"]),
      shape: "rect",
      tone: workflowVisited ? "visited" : "idle",
      detail: agentHubNodeDetail("agent:callable_cards", "Callable cards", "Agent cards without state-gate preconditions or explicit interrupt routes."),
    })
    unroutedCards.forEach((card) => {
      edges.push({ from: hubId, to: cardNodeId(card), label: "available", active: card.id === selectedCard })
    })
  }

  cards.forEach((card) => {
    const phases = Array.isArray(card.phases) ? card.phases : []
    if (phases.length === 0) return

    const isSelectedCard = card.id === selectedCard
    const phaseIds = new Set(phases.map((phase) => phase.id))
    const activePhaseIndex = phases.findIndex((phase) => phase.id === activePhase)
    const inferVisitedFromActivePhase = isSelectedCard && activePhaseIndex >= 0

    phases.forEach((phase, index) => {
      const requirements = latestRequirements.get(phase.id) ?? []
      const blocked = requirements.some((req) => req.met !== true)
      const isActivePhase = isSelectedCard && phase.id === activePhase
      const currentIsTerminalEnd = isActivePhase && cardEnded && !!phase.end_status
      const inferredVisited = inferVisitedFromActivePhase && index <= activePhaseIndex
      const visited = isSelectedCard && (visitedPhaseIds.has(phase.id) || isActivePhase || inferredVisited)
      const phaseNodeId = addMermaidNode(nodes, {
        key: `phase:${card.id}:${phase.id}`,
        label: phaseMermaidLabel(phase, trace, visited),
        shape: mermaidShapeForPhase(phase),
        tone: currentIsTerminalEnd ? "visited" : isActivePhase ? "current" : visited ? "visited" : blocked ? "blocked" : "idle",
        detail: phaseNodeDetail(outline, card, phase, trace, {
          active: isActivePhase,
          visited,
          blocked,
          ended: currentIsTerminalEnd,
        }),
      })

      if (index === 0) {
        edges.push({ from: cardNodeId(card), to: phaseNodeId, active: isSelectedCard && visited })
      }

      const branches = Array.isArray(phase.branches) ? phase.branches : []
      if (branches.length > 0) {
        branches.forEach((branch) => {
          if (!branch.next) return
          const branchTargetIndex = phases.findIndex((row) => row.id === branch.next)
          const branchTargetVisited = visitedPhaseIds.has(branch.next) ||
            (inferVisitedFromActivePhase && branchTargetIndex >= 0 && branchTargetIndex <= activePhaseIndex)
          const target = phaseIds.has(branch.next)
            ? mermaidNodeId(`phase:${card.id}:${branch.next}`)
            : addExternalMermaidNode(nodes, card.id, branch.next)
          edges.push({
            from: phaseNodeId,
            to: target,
            label: branchLabel(branch, phase, trace),
            active: isSelectedCard && visited && branchTargetVisited,
          })
        })
      }

      if (phase.type !== "branch" && !phase.end_status && phase.next && phase.next !== phase.id && phase.next !== "end") {
        const target = phaseIds.has(phase.next)
          ? mermaidNodeId(`phase:${card.id}:${phase.next}`)
          : addExternalMermaidNode(nodes, card.id, phase.next)
        edges.push({ from: phaseNodeId, to: target, label: "next", active: isSelectedCard && visitedPhaseIds.has(phase.next) })
      } else if (branches.length === 0 && !phase.end_status && phases[index + 1]) {
        edges.push({
          from: phaseNodeId,
          to: mermaidNodeId(`phase:${card.id}:${phases[index + 1].id}`),
          active: isSelectedCard && visitedPhaseIds.has(phases[index + 1].id),
        })
      }

      if (phase.end_status) {
        const endNodeId = addMermaidNode(nodes, {
          key: `end:${card.id}:${phase.id}`,
          label: joinMermaidLines(["End:", humanize(phase.end_status)]),
          shape: "rect",
          tone: currentIsTerminalEnd ? "current" : visited ? "visited" : "idle",
          detail: endNodeDetail(card, phase, currentIsTerminalEnd),
        })
        edges.push({ from: phaseNodeId, to: endNodeId, label: "end", active: visited })
      }
    })
  })

  return toMermaidGraph(nodes, edges)
}

function buildMermaidFromOutline(outline: WorkflowGraphOutline, trace: WorkflowTracePayload | null | undefined) {
  const cards = Array.isArray(outline.cards) ? outline.cards : []
  if (cards.length === 0) return emptyMermaidGraph()

  const state = trace?.state ?? {}
  const rawVisitedPhaseIds = visitedPhases(trace)
  const rawStartVisited = startActionsVisited(trace)
  const rawActiveCard = stringish(state.active_card_id)
  const cardEnded = stringish(state.card_status) === "ended"
  const triggerVisited = workflowTriggered(trace) || rawStartVisited || rawVisitedPhaseIds.size > 0 || !!rawActiveCard
  const activeCard = triggerVisited ? rawActiveCard : null
  const activePhase = null
  const visitedPhaseIds = triggerVisited ? rawVisitedPhaseIds : new Set<string>()
  const latestRequirements = requirementsByPhase(trace)
  const selectedCard = triggerVisited ? activeCard || selectedCardFromTrace(trace) : null
  const downstreamVisited = rawVisitedPhaseIds.size > 0 || !!activePhase || !!selectedCard
  const startVisited = triggerVisited && (rawStartVisited || downstreamVisited)
  const nodes: MermaidNode[] = []
  const edges: MermaidEdge[] = []

  const triggerNodeId = addMermaidNode(nodes, {
    key: "trigger",
    label: joinMermaidLines(["Trigger:", triggerName(outline, trace)]),
    shape: "circle",
    tone: triggerVisited ? "visited" : "idle",
    detail: triggerNodeDetail(outline, trace, triggerVisited),
  })

  let source = triggerNodeId
  const startActions = Array.isArray(outline.start_actions) ? outline.start_actions : []
  if (startActions.length > 0) {
    const startNodeId = addMermaidNode(nodes, {
      key: "start",
      label: joinMermaidLines(startActionsLabel(startActions)),
      shape: "rect",
      tone: startVisited ? "visited" : "idle",
      detail: startActionsNodeDetail(startActions, startVisited),
    })
    edges.push({ from: triggerNodeId, to: startNodeId, active: startVisited })
    source = startNodeId
  }

  cards.forEach((card) => {
    const phases = Array.isArray(card.phases) ? card.phases : []
    if (phases.length === 0) return

    const isSelectedCard = card.id === selectedCard
    let cardSource = source
    if (cards.length > 1) {
      const cardNodeId = addMermaidNode(nodes, {
        key: `card:${card.id}`,
        label: joinMermaidLines([humanize(card.id), card.purpose ? truncate(card.purpose, 58) : ""]),
        shape: "rect",
        tone: isSelectedCard ? "visited" : "idle",
        detail: cardNodeDetail(card, isSelectedCard),
      })
      edges.push({ from: source, to: cardNodeId, label: card.preconditions_present ? "matches" : undefined, active: triggerVisited && isSelectedCard })
      cardSource = cardNodeId
    }

    const phaseIds = new Set(phases.map((phase) => phase.id))
    const activePhaseIndex = phases.findIndex((phase) => phase.id === activePhase)
    const inferVisitedFromActivePhase = isSelectedCard && activePhaseIndex >= 0
    const firstPhaseUpstreamVisited = cards.length > 1 ? isSelectedCard : startActions.length > 0 ? startVisited : triggerVisited
    phases.forEach((phase, index) => {
      const requirements = latestRequirements.get(phase.id) ?? []
      const blocked = requirements.some((req) => req.met !== true)
      const isActivePhase = isSelectedCard && phase.id === activePhase
      const currentIsTerminalEnd = isActivePhase && cardEnded && !!phase.end_status
      const inferredVisited = inferVisitedFromActivePhase && index <= activePhaseIndex
      const visited = isSelectedCard && (visitedPhaseIds.has(phase.id) || isActivePhase || inferredVisited)
      const phaseNodeId = addMermaidNode(nodes, {
        key: `phase:${card.id}:${phase.id}`,
        label: phaseMermaidLabel(phase, trace, visited),
        shape: mermaidShapeForPhase(phase),
        tone: currentIsTerminalEnd ? "visited" : isActivePhase ? "current" : visited ? "visited" : blocked ? "blocked" : "idle",
        detail: phaseNodeDetail(outline, card, phase, trace, {
          active: isActivePhase,
          visited,
          blocked,
          ended: currentIsTerminalEnd,
        }),
      })

      if (index === 0) {
        edges.push({ from: cardSource, to: phaseNodeId, active: firstPhaseUpstreamVisited && visited })
      }

      const branches = Array.isArray(phase.branches) ? phase.branches : []
      if (branches.length > 0) {
        branches.forEach((branch) => {
          if (!branch.next) return
          const branchTargetIndex = phases.findIndex((row) => row.id === branch.next)
          const branchTargetVisited = visitedPhaseIds.has(branch.next) ||
            (inferVisitedFromActivePhase && branchTargetIndex >= 0 && branchTargetIndex <= activePhaseIndex)
          const target = phaseIds.has(branch.next)
            ? mermaidNodeId(`phase:${card.id}:${branch.next}`)
            : addExternalMermaidNode(nodes, card.id, branch.next)
          edges.push({
            from: phaseNodeId,
            to: target,
            label: branchLabel(branch, phase, trace),
            active: isSelectedCard && visited && branchTargetVisited,
          })
        })
      }

      if (phase.type !== "branch" && !phase.end_status && phase.next && phase.next !== phase.id && phase.next !== "end") {
        const target = phaseIds.has(phase.next)
          ? mermaidNodeId(`phase:${card.id}:${phase.next}`)
          : addExternalMermaidNode(nodes, card.id, phase.next)
        edges.push({ from: phaseNodeId, to: target, label: "next", active: isSelectedCard && visitedPhaseIds.has(phase.next) })
      } else if (branches.length === 0 && !phase.end_status && phases[index + 1]) {
        edges.push({
          from: phaseNodeId,
          to: mermaidNodeId(`phase:${card.id}:${phases[index + 1].id}`),
          active: isSelectedCard && visitedPhaseIds.has(phases[index + 1].id),
        })
      }

      if (phase.end_status) {
        const endNodeId = addMermaidNode(nodes, {
          key: `end:${card.id}:${phase.id}`,
          label: joinMermaidLines(["End:", humanize(phase.end_status)]),
          shape: "rect",
          tone: currentIsTerminalEnd ? "current" : visited ? "visited" : "idle",
          detail: endNodeDetail(card, phase, currentIsTerminalEnd),
        })
        edges.push({ from: phaseNodeId, to: endNodeId, label: "end", active: visited })
      }
    })
  })

  return toMermaidGraph(nodes, edges)
}

function buildMermaidFromTrace(trace: WorkflowTracePayload | null | undefined) {
  const events = Array.isArray(trace?.trace) ? trace.trace : []
  const state = trace?.state ?? {}
  const activePhase = null
  const activeCard = stringish(state.active_card_id)
  const activeSkill = skillLabelForTrace(events, state)
  const phaseOrder = orderedPhaseIds(events, activePhase)
  const latestRequirements = requirementsByPhase(trace)
  const nodes: MermaidNode[] = []
  const edges: MermaidEdge[] = []

  const skillNodeId = addMermaidNode(nodes, {
    key: "trace:skill",
    label: joinMermaidLines(["Trigger:", activeSkill.title || "Workflow"]),
    shape: "circle",
    tone: events.length > 0 ? "visited" : "idle",
    detail: {
      id: mermaidNodeId("trace:skill"),
      title: "Trigger",
      subtitle: activeSkill.title || "Workflow",
      rows: compactDetailRows([
        ["Skill", activeSkill.title],
        ["Scope", activeSkill.subtitle],
        ["Events", events.length],
      ]),
      sections: [],
    },
  })

  let source = skillNodeId
  if (activeCard) {
    const cardNodeId = addMermaidNode(nodes, {
      key: `trace:card:${activeCard}`,
      label: humanize(activeCard),
      shape: "rect",
      tone: "visited",
      detail: {
        id: mermaidNodeId(`trace:card:${activeCard}`),
        title: humanize(activeCard),
        subtitle: "Selected card",
        rows: compactDetailRows([
          ["Card", activeCard],
          ["State", "visited"],
        ]),
        sections: [],
      },
    })
    edges.push({ from: source, to: cardNodeId, label: "selected", active: true })
    source = cardNodeId
  }

  phaseOrder.forEach((phaseId, index) => {
    const requirements = latestRequirements.get(phaseId) ?? []
    const blocked = requirements.some((req) => req.met !== true)
    const phaseNodeId = addMermaidNode(nodes, {
      key: `trace:phase:${phaseId}`,
      label: joinMermaidLines([humanize(phaseId), ...phaseRuntimeLines(phaseId, trace)]),
      shape: phaseTypeFor(events, phaseId) === "branch" ? "decision" : "rect",
      tone: phaseId === activePhase ? "current" : "visited",
      detail: tracePhaseNodeDetail(phaseId, trace, phaseId === activePhase, blocked),
    })
    edges.push({
      from: index === 0 ? source : mermaidNodeId(`trace:phase:${phaseOrder[index - 1]}`),
      to: phaseNodeId,
      label: index === 0 ? "phase" : "next",
      active: true,
    })
  })

  return nodes.length > 1 ? toMermaidGraph(nodes, edges) : emptyMermaidGraph()
}

function emptyMermaidGraph(): MermaidGraph {
  return { diagram: "", details: [] }
}

function toMermaidGraph(nodes: MermaidNode[], edges: MermaidEdge[]): MermaidGraph {
  return {
    diagram: renderMermaid(nodes, edges),
    details: nodes.map((node) => node.detail).filter((detail): detail is MermaidNodeDetail => !!detail),
  }
}

function renderMermaid(nodes: MermaidNode[], edges: MermaidEdge[]) {
  const lines = [
    "flowchart TD",
    "  classDef idle fill:#18181b,stroke:#71717a,color:#e5e7eb;",
    "  classDef visited fill:#0f3d22,stroke:#22c55e,color:#dcfce7,stroke-width:2px;",
    "  classDef current fill:#082f49,stroke:#38bdf8,color:#e0f2fe,stroke-width:3px;",
    "  classDef blocked fill:#3a1515,stroke:#ef4444,color:#fee2e2,stroke-width:3px;",
    "  classDef warning fill:#33230b,stroke:#f59e0b,color:#fef3c7,stroke-width:2px;",
  ]

  nodes.forEach((node) => lines.push(`  ${renderMermaidNode(node)}`))
  edges.forEach((edgeRow) => {
    const label = edgeRow.label ? `|${mermaidEdgeLabel(edgeRow.label)}|` : ""
    lines.push(`  ${edgeRow.from} -->${label} ${edgeRow.to}`)
  })

  ;(["idle", "visited", "current", "blocked", "warning"] as MermaidTone[]).forEach((tone) => {
    const ids = nodes.filter((node) => node.tone === tone).map((node) => node.id)
    if (ids.length > 0) lines.push(`  class ${ids.join(",")} ${tone};`)
  })

  edges.forEach((edgeRow, index) => {
    if (edgeRow.active) lines.push(`  linkStyle ${index} stroke:#22c55e,stroke-width:3px;`)
  })

  return lines.join("\n")
}

function renderMermaidNode(node: MermaidNode) {
  const label = mermaidLabel(node.label)
  if (node.shape === "circle") return `${node.id}(("${label}"))`
  if (node.shape === "data") return `${node.id}[/"${label}"/]`
  if (node.shape === "decision") return `${node.id}{"${label}"}`
  return `${node.id}["${label}"]`
}

function addMermaidNode(nodes: MermaidNode[], node: Omit<MermaidNode, "id">) {
  const id = mermaidNodeId(node.key)
  if (!nodes.some((existing) => existing.id === id)) {
    const row = { ...node, id }
    nodes.push({ ...row, detail: row.detail ?? defaultMermaidNodeDetail(row) })
  }
  return id
}

function defaultMermaidNodeDetail(node: MermaidNode): MermaidNodeDetail {
  const lines = node.label.split("\n").filter(Boolean)
  return {
    id: node.id,
    title: lines[0] || humanize(node.key),
    subtitle: node.shape,
    rows: [
      { label: "Key", value: node.key },
      { label: "State", value: node.tone },
    ],
    sections: [],
  }
}

function addExternalMermaidNode(nodes: MermaidNode[], cardId: string, target: string) {
  return addMermaidNode(nodes, {
    key: `external:${cardId}:${target}`,
    label: joinMermaidLines(["Missing phase:", humanize(target)]),
    shape: "rect",
    tone: "warning",
  })
}

function mermaidNodeId(key: string) {
  return `n_${Math.abs(hashString(key))}_${key.replace(/[^a-zA-Z0-9_]/g, "_").slice(0, 32)}`
}

function triggerName(outline: WorkflowGraphOutline, trace: WorkflowTracePayload | null | undefined) {
  const trigger = outline.trigger ?? {}
  return stringish(trigger.intent || trigger.type) || skillLabelForTrace(Array.isArray(trace?.trace) ? trace.trace : [], trace?.state ?? {}).title || "workflow"
}

function triggerNodeDetail(outline: WorkflowGraphOutline, trace: WorkflowTracePayload | null | undefined, triggered: boolean): MermaidNodeDetail {
  const trigger = outline.trigger ?? {}
  return {
    id: mermaidNodeId("trigger"),
    title: "Trigger",
    subtitle: triggerName(outline, trace),
    description: "This is the workflow entrance condition. Once it matches, the runtime can select a card and start moving through phases.",
    rows: compactDetailRows([
      ["State", triggered ? "matched" : "not reached"],
      ["Type", trigger.type],
      ["Intent", trigger.intent],
      ["Tool", trigger.tool],
    ]),
    sections: Object.keys(trigger).length > 0 ? [{ title: "Trigger config", value: trigger }] : [],
  }
}

function startActionsNodeDetail(actions: WorkflowGraphAction[], visited: boolean): MermaidNodeDetail {
  return {
    id: mermaidNodeId("start"),
    title: "Start actions",
    subtitle: "Workflow setup",
    description: "These actions run at workflow start so later phases can make deterministic decisions from collected data.",
    rows: compactDetailRows([
      ["State", visited ? "completed" : "not reached"],
      ["Endpoints", actions.filter((action) => action.endpoint).map((action) => action.endpoint).join(", ")],
      ["Tools", actions.filter((action) => action.tool).map((action) => action.tool).join(", ")],
      ["Policies", actions.filter((action) => action.policy).map((action) => action.policy).join(", ")],
    ]),
    sections: [{ title: "Actions", value: actions }],
  }
}

function agentHubNodeDetail(key: string, title: string, description: string): MermaidNodeDetail {
  return {
    id: mermaidNodeId(key),
    title,
    subtitle: "Agent routing",
    description,
    rows: [],
    sections: [],
  }
}

function interruptTargetCard(interrupt: WorkflowGraphInterrupt) {
  return stringish(interrupt.card || interrupt.start_card)
}

function interruptTitle(interrupt: WorkflowGraphInterrupt) {
  const tool = stringish(interrupt.trigger?.tool)
  if (tool === "detect_human_request") return "Human request"
  if (tool === "detect_frustration") return "Severe frustration"
  return humanize(interrupt.id || tool || "interrupt")
}

function interruptTriggerLine(interrupt: WorkflowGraphInterrupt) {
  const trigger = asRecord(interrupt.trigger)
  const tool = stringish(trigger.tool)
  const conditions = asRecord(trigger.conditions)
  const severity = stringish(conditions.severity_gte)
  const pieces = [
    tool ? humanize(tool) : stringish(trigger.type),
    severity ? `severity >= ${severity}` : null,
  ].filter((value): value is string => !!value)
  return pieces.join(" · ")
}

function interruptNodeDetail(interrupt: WorkflowGraphInterrupt, selected: boolean): MermaidNodeDetail {
  const trigger = asRecord(interrupt.trigger)
  return {
    id: mermaidNodeId(`interrupt:${interrupt.id}`),
    title: interruptTitle(interrupt),
    subtitle: "Global interrupt",
    description: interrupt.purpose || "This runtime interrupt can route the conversation into a protected card.",
    rows: compactDetailRows([
      ["State", selected ? "matched" : "not reached"],
      ["Interrupt", interrupt.id],
      ["Trigger type", trigger.type],
      ["Tool", trigger.tool],
      ["Card", interruptTargetCard(interrupt)],
      ["Start skill", interrupt.start_skill],
    ]),
    sections: Object.keys(trigger).length > 0 ? [{ title: "Trigger", value: trigger }] : [],
  }
}

function gateNodeDetail(card: WorkflowGraphCard, selected: boolean): MermaidNodeDetail {
  return {
    id: mermaidNodeId(`gate:${card.id}`),
    title: humanize(card.id),
    subtitle: "State gate",
    description: "This entrance rule routes matching users into a protected agent card.",
    rows: compactDetailRows([
      ["State", selected ? "matched" : "not reached"],
      ["Card", card.id],
      ["Priority", card.priority],
      ["Rule", card.preconditions_summary],
    ]),
    sections: [],
  }
}

function cardNodeDetail(card: WorkflowGraphCard, selected: boolean): MermaidNodeDetail {
  return {
    id: mermaidNodeId(`card:${card.id}`),
    title: humanize(card.id),
    subtitle: selected ? "Selected card" : "Card",
    description: card.purpose || "A card is one deterministic path through the skill workflow.",
    rows: compactDetailRows([
      ["State", selected ? "selected" : "not selected"],
      ["Priority", card.priority],
      ["Entrance rules", card.preconditions_present ? "present" : "none"],
      ["Phases", Array.isArray(card.phases) ? card.phases.length : 0],
    ]),
    sections: [],
  }
}

function phaseNodeDetail(
  outline: WorkflowGraphOutline,
  card: WorkflowGraphCard,
  phase: WorkflowGraphPhase,
  trace: WorkflowTracePayload | null | undefined,
  state: { active: boolean; visited: boolean; blocked: boolean; ended: boolean },
): MermaidNodeDetail {
  const policyAction = phasePolicyAction(phase)
  const policyDefinition = policyAction ? outline.policies?.[actionLabel(policyAction)] : null
  const policy = latestPolicyEventForPhase(phase.id, trace)
  const policyData = policy ? policySummary(policy) : null
  const requirements = requirementsByPhase(trace).get(phase.id) ?? []
  const blockers = requirements.filter((req) => req.met !== true).map(requirementLabel)
  const fieldEvent = latestFieldEventForPhase(phase.id, trace)
  const rows = compactDetailRows([
    ["State", state.ended ? "ended" : state.active ? "current" : state.visited ? "visited" : state.blocked ? "blocked" : "not reached"],
    ["Card", card.id],
    ["Phase", phase.id],
    ["Type", phase.type],
    ["Purpose", phase.purpose],
    ["Policy", policyAction ? actionLabel(policyAction) : null],
    ["Verdict", policyData?.verdict],
    ["Waiting on", blockers.join(", ")],
    ["Required fields", phase.required_fields?.join(", ")],
    ["Required upload", stringish(phase.required_upload?.kind)],
    ["Tools", phase.tools?.join(", ")],
    ["Next", phase.next],
    ["End status", phase.end_status],
  ])
  const sections: MermaidNodeDetail["sections"] = []
  if (policyAction) sections.push({ title: "Policy action", value: policyAction })
  if (policyDefinition) sections.push({ title: "Policy logic", value: policyLogicForModal(policyDefinition) })
  if (policyData?.inputs != null) sections.push({ title: "Policy inputs", value: policyData.inputs })
  if (policyData?.result != null) sections.push({ title: "Policy result", value: policyData.result })
  if (fieldEvent) sections.push({ title: "Collected fields", value: asRecord(fieldEvent.details?.fields) })
  if (requirements.length > 0) sections.push({ title: "Requirements", value: requirements })
  if (Array.isArray(phase.branches) && phase.branches.length > 0) sections.push({ title: "Branches", value: phase.branches })
  if (Array.isArray(phase.actions) && phase.actions.length > 0 && !policyAction) sections.push({ title: "Actions", value: phase.actions })

  return {
    id: mermaidNodeId(`phase:${card.id}:${phase.id}`),
    title: policyAction ? `Policy: ${actionLabel(policyAction)}` : humanize(phase.id),
    subtitle: phase.type || "phase",
    description: policyAction
      ? "This policy evaluates runtime data and returns a verdict. The next branch uses that verdict to decide where the conversation goes."
      : phase.purpose,
    rows,
    sections,
  }
}

function endNodeDetail(card: WorkflowGraphCard, phase: WorkflowGraphPhase, current: boolean): MermaidNodeDetail {
  return {
    id: mermaidNodeId(`end:${card.id}:${phase.id}`),
    title: `End: ${humanize(phase.end_status)}`,
    subtitle: current ? "Current terminal state" : "Terminal state",
    description: "The workflow card has reached a terminal outcome.",
    rows: compactDetailRows([
      ["State", current ? "current" : "not current"],
      ["Card", card.id],
      ["Source phase", phase.id],
      ["End status", phase.end_status],
    ]),
    sections: [],
  }
}

function tracePhaseNodeDetail(phaseId: string, trace: WorkflowTracePayload | null | undefined, active: boolean, blocked: boolean): MermaidNodeDetail {
  const policy = latestPolicyEventForPhase(phaseId, trace)
  const policyData = policy ? policySummary(policy) : null
  const requirements = requirementsByPhase(trace).get(phaseId) ?? []
  const fieldEvent = latestFieldEventForPhase(phaseId, trace)
  const rows = compactDetailRows([
    ["State", active ? "current" : blocked ? "blocked" : "visited"],
    ["Phase", phaseId],
    ["Type", phaseTypeFor(Array.isArray(trace?.trace) ? trace.trace : [], phaseId)],
    ["Verdict", policyData?.verdict],
    ["Waiting on", requirements.filter((req) => req.met !== true).map(requirementLabel).join(", ")],
  ])
  const sections: MermaidNodeDetail["sections"] = []
  if (policyData?.inputs != null) sections.push({ title: "Policy inputs", value: policyData.inputs })
  if (policyData?.result != null) sections.push({ title: "Policy result", value: policyData.result })
  if (fieldEvent) sections.push({ title: "Collected fields", value: asRecord(fieldEvent.details?.fields) })
  if (requirements.length > 0) sections.push({ title: "Requirements", value: requirements })

  return {
    id: mermaidNodeId(`trace:phase:${phaseId}`),
    title: policyData ? `Policy: ${policyData.label}` : humanize(phaseId),
    subtitle: "Runtime phase",
    description: policyData ? "This policy evaluated runtime data and returned the verdict shown above." : undefined,
    rows,
    sections,
  }
}

function policyLogicForModal(policy: WorkflowGraphPolicy) {
  const rules = Array.isArray(policy.rules) ? policy.rules : []
  return rules.map((rule, index) => ({
    rule: rule.name || `rule_${index + 1}`,
    if: predicateLogicLabel(rule.when) || rule.when_summary || rule.when,
    verdict: rule.verdict || stringish(rule.then?.verdict),
  }))
}

function predicateLogicLabel(predicate: unknown): string | null {
  const h = asRecord(predicate)
  const entries = Object.entries(h)
  if (entries.length !== 1) return null

  const [key, value] = entries[0]
  if (key === "all" && Array.isArray(value)) {
    return value.map(predicateLogicLabel).filter(Boolean).join(" AND ") || null
  }
  if (key === "any" && Array.isArray(value)) {
    return value.map(predicateLogicLabel).filter(Boolean).join(" OR ") || null
  }
  if (key === "not") {
    const nested = predicateLogicLabel(value)
    return nested ? `NOT (${nested})` : null
  }

  const expression = asRecord(value)
  const opEntry = Object.entries(expression)[0]
  if (!opEntry) return null

  const [operator, expected] = opEntry
  return `${key} ${operator} ${shortValue(expected)}`
}

function phaseMermaidLabel(phase: WorkflowGraphPhase, trace: WorkflowTracePayload | null | undefined, visited: boolean) {
  const policyAction = phasePolicyAction(phase)
  const title = policyAction ? `Policy: ${actionLabel(policyAction)}` : phase.purpose ? truncate(phase.purpose, 64) : humanize(phase.id)
  const lines = [title]
  if (!visited) {
    const detail = staticPhaseDetail(phase)
    if (detail) lines.push(detail)
    return joinMermaidLines(lines)
  }
  return joinMermaidLines([...lines, ...phaseRuntimeLines(phase.id, trace)])
}

function staticPhaseDetail(phase: WorkflowGraphPhase) {
  if (phase.type === "collect_fields" && Array.isArray(phase.required_fields) && phase.required_fields.length > 0) {
    return `collect ${phase.required_fields.join(" + ")}`
  }
  if (phase.type === "collect_upload") return `collect ${stringish(phase.required_upload?.kind) || "upload"}`
  if (phase.type === "action" && Array.isArray(phase.actions) && phase.actions.length > 0) {
    const policyAction = phasePolicyAction(phase)
    if (policyAction) return "Verdict: pending"
    return phase.actions.map(actionLabel).slice(0, 2).join(" + ")
  }
  if (phase.type === "branch") return "verdict pending"
  if (phase.end_status) return `end: ${phase.end_status}`
  return phase.type
}

function phaseRuntimeLines(phaseId: string, trace: WorkflowTracePayload | null | undefined) {
  const events = Array.isArray(trace?.trace) ? trace.trace : []
  const lines: string[] = []
  const fieldEvent = latestFieldEventForPhase(phaseId, trace)
  Object.entries(asRecord(fieldEvent?.details?.fields)).slice(0, 3).forEach(([key, value]) => lines.push(`${key}: ${shortValue(value)}`))

  const policy = latestPolicyEventForPhase(phaseId, trace)
  const policyVerdict = policy ? policySummary(policy)?.verdict : null
  if (policyVerdict) lines.push(`Verdict: ${policyVerdict}`)

  const branchVerdict = policyVerdict || latestPolicyVerdict(trace)
  if (!policyVerdict && branchVerdict && phaseId.includes("verdict")) lines.push(`Verdict: ${branchVerdict}`)

  const action = [...events].reverse().find((event) => eventPhaseId(event) === phaseId && actionEvent(event))
  if (action && !policyVerdict && action.event !== "collect_fields_matched") lines.push(traceEventTitle(action))

  const requirements = requirementsByPhase(trace).get(phaseId) ?? []
  const blockers = requirements.filter((req) => req.met !== true).map(requirementLabel)
  if (blockers.length > 0) lines.push(`waiting: ${blockers.slice(0, 2).join(", ")}`)

  return lines.slice(0, 5)
}

function latestPolicyEventForPhase(phaseId: string, trace: WorkflowTracePayload | null | undefined) {
  const events = Array.isArray(trace?.trace) ? trace.trace : []
  return [...events].reverse().find((event) => eventPhaseId(event) === phaseId && isPolicyEvent(event)) ?? null
}

function latestFieldEventForPhase(phaseId: string, trace: WorkflowTracePayload | null | undefined) {
  const events = Array.isArray(trace?.trace) ? trace.trace : []
  return [...events].reverse().find((event) => event.event === "collect_fields_matched" && eventPhaseId(event) === phaseId) ?? null
}

function mermaidShapeForPhase(phase: WorkflowGraphPhase): MermaidShape {
  if (phase.type === "collect_fields" || phase.type === "collect_upload") return "data"
  if (phase.type === "branch") return "decision"
  return "rect"
}

function startActionsVisited(trace: WorkflowTracePayload | null | undefined) {
  return Array.isArray(trace?.trace) && trace.trace.some((event) => {
    const owner = stringish(event.details?.owner)
    return owner === "start.collect" && ["action_started", "action_completed", "action_failed"].includes(event.event)
  })
}

function workflowTriggered(trace: WorkflowTracePayload | null | undefined) {
  return Array.isArray(trace?.trace) && trace.trace.some((event) => {
    return ["workflow_candidates_scored", "workflow_activated", "agent_workflow_activated", "skill_card_activated"].includes(event.event)
  })
}

function selectedCardFromTrace(trace: WorkflowTracePayload | null | undefined) {
  if (!Array.isArray(trace?.trace)) return null
  const row = [...trace.trace].reverse().find((event) => event.event === "skill_card_activated")
  return stringish(row?.details?.skill_key || row?.details?.card_id)
}

function actionLabel(action: WorkflowGraphAction) {
  return stringish(action.endpoint || action.tool || action.policy || action.matcher || action.id || action.type) || "action"
}

function phasePolicyAction(phase: WorkflowGraphPhase) {
  return Array.isArray(phase.actions) ? phase.actions.find((action) => action.type === "policy" || action.policy) : undefined
}

function startActionsLabel(actions: WorkflowGraphAction[]) {
  const endpoints = actions.filter((action) => action.endpoint).map((action) => String(action.endpoint))
  const tools = actions.filter((action) => action.tool).map((action) => String(action.tool))
  if (endpoints.length > 0 && tools.length === 0) return ["Endpoints (GET)", ...endpoints.slice(0, 4)]
  if (endpoints.length > 0) return ["Start actions", ...endpoints.map((key) => `GET ${key}`).slice(0, 3), ...tools.map((key) => `tool ${key}`).slice(0, 2)]
  return ["Start actions", ...actions.map(actionLabel).slice(0, 4)]
}

function joinMermaidLines(lines: Array<string | null | undefined>) {
  return lines.map((line) => line?.trim()).filter((line): line is string => !!line).join("\n")
}

function mermaidLabel(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br/>")
}

function mermaidEdgeLabel(value: string) {
  return truncate(value.replace(/\|/g, "/").replace(/\n/g, " "), 34)
}

function hashString(value: string) {
  let hash = 0
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0
  }
  return hash
}

function branchLabel(branch: WorkflowGraphBranch, phase?: WorkflowGraphPhase, trace?: WorkflowTracePayload | null | undefined) {
  if (branch.kind === "blocked" && branch.next) return branch.next
  if (branch.purpose) return branch.purpose
  const fromPredicate = branchVerdictLabel(branch.predicate)
  if (fromPredicate) return fromPredicate
  const next = stringish(branch.next)
  if (next) return humanize(next).replace(/^request /, "request ")
  if (phase?.id?.includes("verdict")) return latestPolicyVerdict(trace) || "matched"
  if (branch.predicate) return truncate(branch.predicate, 24)
  return `branch ${branch.index ?? ""}`.trim()
}

function branchVerdictLabel(predicate?: string) {
  const value = stringish(predicate)
  if (!value) return null
  const verdict = value.match(/\bverdict\b\s+(?:eq|=|==)\s+([a-zA-Z0-9_]+)/i)?.[1]
  if (verdict) return verdict
  if (value.includes("ally_credit_used")) return "already credited"
  if (value.includes("require_video")) return "request video"
  if (/\bauto_credit\b/.test(value)) return "auto credit"
  return null
}

function latestPolicyVerdict(trace: WorkflowTracePayload | null | undefined) {
  if (!Array.isArray(trace?.trace)) return null
  const policy = [...trace.trace].reverse().find(isPolicyEvent)
  return policy ? policySummary(policy)?.verdict || null : null
}

function visitedPhases(trace: WorkflowTracePayload | null | undefined) {
  const set = new Set<string>()
  Array.isArray(trace?.trace) && trace.trace.forEach((event) => {
    const phase = eventPhaseId(event)
    if (phase) set.add(phase)
    const to = stringish(event.details?.to_phase_id)
    if (to) set.add(to)
  })
  return set
}

function requirementsByPhase(trace: WorkflowTracePayload | null | undefined) {
  const map = new Map<string, WorkflowTraceRequirement[]>()
  if (!Array.isArray(trace?.trace)) return map
  trace.trace.forEach((event) => {
    const phase = eventPhaseId(event)
    const requirements = Array.isArray(event.details?.requirements) ? event.details.requirements : []
    if (phase && requirements.length > 0) map.set(phase, requirements)
  })
  return map
}

function orderedPhaseIds(events: WorkflowTraceEvent[], activePhase: string | null) {
  const ids: string[] = []
  events.forEach((event) => {
    const from = stringish(event.details?.from_phase_id)
    const to = stringish(event.details?.to_phase_id)
    const phase = eventPhaseId(event)
    ;[from, phase, to].forEach((id) => {
      if (id && !ids.includes(id)) ids.push(id)
    })
  })
  if (activePhase && !ids.includes(activePhase)) ids.push(activePhase)
  return ids
}

function phaseTypeFor(events: WorkflowTraceEvent[], phaseId: string) {
  const event = events.find((row) => eventPhaseId(row) === phaseId && row.details?.phase_type)
  return stringish(event?.details?.phase_type)
}

function eventPhaseId(event: WorkflowTraceEvent) {
  const owner = stringish(event.details?.owner)
  return stringish(event.details?.phase_id || (asRecord(event.details?.contract).phase)) ||
    (owner && !owner.includes(".") ? owner : null)
}

function actionEvent(event: WorkflowTraceEvent) {
  return [
    "action_started",
    "action_completed",
    "action_failed",
    "model_tool_action_waiting",
    "model_tool_action_completed",
    "collect_fields_matched",
    "dropped_unavailable_tool_calls",
    "workflow_error",
    "workflow_candidates_scored",
    "agent_workflow_activated",
    "workflow_activated",
    "skill_card_activated",
  ].includes(event.event)
}

function traceEventTitle(event: WorkflowTraceEvent) {
  const details = asRecord(event.details)
  const result = asRecord(details.result)
  const endpoint = stringish(details.endpoint || result.endpoint)
  const tool = stringish(details.tool || result.tool)
  const policy = stringish(details.policy || result.policy)
  const selected = stringish(details.selected_skill_version_id)
  if (event.event === "workflow_candidates_scored") return selected ? `selected version ${selected}` : "no workflow matched"
  if (event.event === "workflow_activated" || event.event === "agent_workflow_activated") return "workflow activated"
  if (event.event === "skill_card_activated") return `card ${stringish(details.skill_key || details.card_id) || "activated"}`
  if (endpoint) return `endpoint ${endpoint}`
  if (tool) return `tool ${tool}`
  if (policy) return `policy ${policy}`
  if (event.event === "collect_fields_matched") return "collected fields"
  return formatEventName(event.event)
}

function skillLabelForTrace(events: WorkflowTraceEvent[], state: Record<string, unknown>) {
  const selectedCandidate = selectedCandidateFromTrace(events, state)
  const activation = [...events].reverse().find((event) => {
    return event.event === "workflow_activated" || event.event === "contract_built" || event.event === "prepare_started"
  })
  const activationDetails = asRecord(activation?.details)
  const key = stringish(selectedCandidate.skill_key || activationDetails.active_skill_key)
  const name = stringish(selectedCandidate.skill_name)
  const scope = stringish(state.active_workflow_scope || activationDetails.active_workflow_scope) || "skill"
  const skillId = stringish(state.active_skill_id || activationDetails.active_skill_id)
  const versionId = stringish(state.active_skill_version_id || activationDetails.active_skill_version_id || selectedCandidate.skill_version_id)
  const title =
    name || key ||
    (scope === "agent" ? "Agent routing" : skillId ? "Selected skill" : "")

  return {
    title,
    subtitle: scope === "agent" ? "agent routing" : "selected skill",
    chips: [
      key && name && key !== name ? key : null,
      versionId ? `version ${versionId}` : null,
      !key && skillId ? `skill id ${skillId}` : null,
    ].filter((value): value is string => !!value),
  }
}

function selectedCandidateFromTrace(events: WorkflowTraceEvent[], state: Record<string, unknown>) {
  const selectedVersion = stringish(state.active_skill_version_id)
  const row = [...events].reverse().find((event) => event.event === "workflow_candidates_scored")
  const details = asRecord(row?.details)
  const selected = stringish(details.selected_skill_version_id) || selectedVersion
  const candidates = Array.isArray(details.candidates) ? details.candidates : []
  const candidate = candidates.map(asRecord).find((entry) => stringish(entry.skill_version_id) === selected)
  return candidate ?? {}
}

function messageLabel(message: ChatBubble) {
  if (message.authorName?.trim()) return message.authorName.trim()
  if (message.role === "assistant") return "Agent"
  if (message.role === "tool") return "Tool"
  if (message.role === "system") return "System"
  if (message.role === "human_agent") return "Human agent"
  if (message.role === "internal_note") return "Internal note"
  return "User"
}

function toolOnlyMessageLabel(message: ChatBubble) {
  const toolCalls = Array.isArray(message.toolCalls) ? message.toolCalls.length : 0
  const toolResults = Array.isArray(message.toolResults) ? message.toolResults.length : 0
  if (toolCalls > 0 && toolResults > 0) return `${toolCalls} tool call${toolCalls === 1 ? "" : "s"}, ${toolResults} result${toolResults === 1 ? "" : "s"}`
  if (toolCalls > 0) return `${toolCalls} tool call${toolCalls === 1 ? "" : "s"}`
  if (toolResults > 0) return `${toolResults} tool result${toolResults === 1 ? "" : "s"}`
  return "No text"
}

function requirementLabel(req: WorkflowTraceRequirement): string {
  return req.field || req.tool || req.upload_kind || req.action_key || req.kind || "requirement"
}

function formatEventName(event: string) {
  return event.replace(/_/g, " ")
}

function truncate(value: string, max: number) {
  return value.length > max ? `${value.slice(0, max - 3)}...` : value
}

function shortValue(value: unknown) {
  if (value == null) return "null"
  if (typeof value === "string") return truncate(value, 48)
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? "" : "s"}`
  const record = asRecord(value)
  const keys = Object.keys(record)
  return keys.length > 0 ? keys.slice(0, 3).join(", ") : String(value)
}

function humanize(value?: string | number | null) {
  return String(value ?? "")
    .replace(/^agent\./, "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

function stringish(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim()
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  return null
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

function normalizeWorkflowGraphOutline(raw: unknown): WorkflowGraphOutline | null {
  const h = asRecord(raw)
  if (Object.keys(h).length === 0) return null

  return {
    engine: stringish(h.engine) || undefined,
    version: h.version as WorkflowGraphOutline["version"],
    purpose: stringish(h.purpose) || undefined,
    trigger: asRecord(h.trigger),
    scope: stringish(h.scope) || undefined,
    blocked_interrupts: Array.isArray(h.blocked_interrupts) ? h.blocked_interrupts.map(String) : [],
    policies: normalizeWorkflowGraphPolicies(h.policies),
    start_actions: Array.isArray(h.start_actions) ? h.start_actions.map(normalizeWorkflowGraphAction) : [],
    interrupts: Array.isArray(h.interrupts) ? h.interrupts.map(normalizeWorkflowGraphInterrupt) : [],
    cards: Array.isArray(h.cards) ? h.cards.map(normalizeWorkflowGraphCard) : [],
  }
}

function normalizeWorkflowGraphPolicies(raw: unknown): Record<string, WorkflowGraphPolicy> {
  const h = asRecord(raw)
  return Object.entries(h).reduce<Record<string, WorkflowGraphPolicy>>((out, [key, value]) => {
    const policy = asRecord(value)
    out[key] = {
      rules: Array.isArray(policy.rules) ? policy.rules.map(normalizeWorkflowGraphPolicyRule) : [],
    }
    return out
  }, {})
}

function normalizeWorkflowGraphPolicyRule(raw: unknown): WorkflowGraphPolicyRule {
  const h = asRecord(raw)
  return {
    name: stringish(h.name) || undefined,
    when: h.when,
    when_summary: stringish(h.when_summary) || undefined,
    verdict: stringish(h.verdict) || undefined,
    then: asRecord(h.then),
  }
}

function normalizeWorkflowGraphInterrupt(raw: unknown): WorkflowGraphInterrupt {
  const h = asRecord(raw)
  return {
    id: String(h.id ?? ""),
    purpose: stringish(h.purpose) || undefined,
    card: stringish(h.card) || undefined,
    start_skill: stringish(h.start_skill) || undefined,
    start_card: stringish(h.start_card) || undefined,
    trigger: asRecord(h.trigger),
  }
}

function normalizeWorkflowGraphCard(raw: unknown): WorkflowGraphCard {
  const h = asRecord(raw)
  return {
    id: String(h.id ?? ""),
    purpose: stringish(h.purpose) || undefined,
    priority: h.priority as WorkflowGraphCard["priority"],
    preconditions_present: h.preconditions_present === true,
    preconditions_summary: stringish(h.preconditions_summary) || undefined,
    phases: Array.isArray(h.phases) ? h.phases.map(normalizeWorkflowGraphPhase) : [],
  }
}

function normalizeWorkflowGraphPhase(raw: unknown): WorkflowGraphPhase {
  const h = asRecord(raw)
  return {
    id: String(h.id ?? ""),
    purpose: stringish(h.purpose) || undefined,
    type: stringish(h.type) || undefined,
    response_mode: stringish(h.response_mode) || undefined,
    next: stringish(h.next) || undefined,
    end_status: stringish(h.end_status) || undefined,
    required_fields: Array.isArray(h.required_fields) ? h.required_fields.map(String) : [],
    optional_fields: Array.isArray(h.optional_fields) ? h.optional_fields.map(String) : [],
    required_upload: asRecord(h.required_upload),
    tools: Array.isArray(h.tools) ? h.tools.map(String) : [],
    actions: Array.isArray(h.actions) ? h.actions.map(normalizeWorkflowGraphAction) : [],
    branches: Array.isArray(h.branches) ? h.branches.map(normalizeWorkflowGraphBranch) : [],
  }
}

function normalizeWorkflowGraphAction(raw: unknown): WorkflowGraphAction {
  const h = asRecord(raw)
  return {
    id: String(h.id ?? ""),
    purpose: stringish(h.purpose) || undefined,
    type: stringish(h.type) || undefined,
    tool: stringish(h.tool) || undefined,
    endpoint: stringish(h.endpoint) || undefined,
    policy: stringish(h.policy) || undefined,
    matcher: stringish(h.matcher) || undefined,
    preconditions_present: h.preconditions_present === true,
  }
}

function normalizeWorkflowGraphBranch(raw: unknown): WorkflowGraphBranch {
  const h = asRecord(raw)
  return {
    kind: stringish(h.kind) || undefined,
    index: typeof h.index === "number" ? h.index : undefined,
    purpose: stringish(h.purpose) || undefined,
    next: stringish(h.next) || undefined,
    predicate: stringish(h.predicate) || undefined,
  }
}
